import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TopologyChannel, TopologyResponse, TopologySource } from '../api/contracts';

/**
 * GET /api/topology
 *
 * Small, human-curated, changes rarely (see contracts.ts) -- a longer
 * staleTime than the app default (30s, set in main.tsx) is deliberate here,
 * this is topology config, not time-series.
 */
export function useTopology() {
  return useQuery({
    queryKey: ['topology'],
    queryFn: () => apiClient.get<TopologyResponse>('/api/topology'),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Containment tree
// ---------------------------------------------------------------------------
//
// `containedBy` is the whole point of this page (see docs/PLAN.md + the
// SHP2/vacant-AC incident): nested channels must render as a TREE, never a
// flat list a user could misread as summable siblings. Per
// api/src/db/seedTopologyDynamo.ts, `containedByKey` (the seed's source of
// `containedBy`) is ALWAYS same-source -- SHP2 circuits nest under the SHP2
// grid-in/load-total channel, never across sources into Emporia. This
// function builds one root-level entry per source, with that source's own
// channels nested beneath it by `containedBy`.

export interface ChannelTreeNode {
  kind: 'channel';
  channel: TopologyChannel;
  children: ChannelTreeNode[];
}

export interface SourceTreeNode {
  kind: 'source';
  source: TopologySource;
  children: ChannelTreeNode[];
}

export type TopologyTreeNode = SourceTreeNode | ChannelTreeNode;

/**
 * Builds the source -> channel containment tree from a raw topology
 * response. Defensive by design: if a channel's `containedBy` points at an
 * id that isn't a sibling channel of the same source (shouldn't happen per
 * seed-topology.ts's same-source invariant, but the API is the contract,
 * not the seed script), the channel is promoted to a root of its source
 * rather than silently dropped. Losing a channel from the displayed tree is
 * exactly the class of bug this page exists to prevent -- a stale/omitted
 * node reads as "doesn't exist," indistinguishable from the vacant-AC
 * incident this whole model was built to catch.
 */
export function buildTopologyTree(data: TopologyResponse): SourceTreeNode[] {
  return data.sources.map((source) => {
    const channels = data.channels.filter((c) => c.sourceId === source.id);
    const byId = new Map(channels.map((c) => [c.id, c] as const));
    const childrenOf = new Map<string | null, TopologyChannel[]>();

    for (const c of channels) {
      const parentKey = c.containedBy !== null && byId.has(c.containedBy) ? c.containedBy : null;
      const list = childrenOf.get(parentKey);
      if (list) list.push(c);
      else childrenOf.set(parentKey, [c]);
    }

    const build = (channel: TopologyChannel): ChannelTreeNode => ({
      kind: 'channel',
      channel,
      children: (childrenOf.get(channel.id) ?? []).map(build),
    });

    const roots = childrenOf.get(null) ?? [];
    return { kind: 'source', source, children: roots.map(build) };
  });
}

// ---------------------------------------------------------------------------
// Vacant / aggregate display rules
// ---------------------------------------------------------------------------

/**
 * True for channels that physically don't exist right now (e.g. the
 * SHP2-positions-1-and-3 stale AC label). Must be de-emphasized in the UI
 * and excluded from anything client-side that resembles a sum or a "live"
 * highlight -- but never hidden, since that would lose the historical
 * record (the exact defence docs/PLAN.md calls for).
 */
export function isVacant(channel: Pick<TopologyChannel, 'state'>): boolean {
  return channel.state === 'vacant';
}

/**
 * True for channels whose value is ALREADY represented by their children
 * (mains, SHP2 load-total, SHP2 grid-in) -- these are aggregates, not
 * additional leaf consumption, and must be visually distinguished so a user
 * does not read the tree and mentally sum a parent plus its children.
 */
export function isAggregate(channel: Pick<TopologyChannel, 'countsTowardTotal'>): boolean {
  return !channel.countsTowardTotal;
}

export interface ChildCountSummary {
  /** Active, counted-toward-total leaf/aggregate children -- these are what a rollup would legitimately include. */
  countedActive: number;
  /** Vacant children -- must never contribute to any count or sum presented as current. */
  vacant: number;
  /** Everything else (e.g. active but countsTowardTotal:false nested aggregates, retired channels). */
  other: number;
  total: number;
}

/**
 * Summarizes a node's direct children by display category, for an
 * informational caption under aggregate nodes (e.g. "10 active, 2 vacant").
 * Deliberately a COUNT, not a wattage sum -- this page prefers not to
 * compute client-side sums at all (see contracts.ts's header + the
 * countsTowardTotal doc comment in seed-topology.ts: the true household
 * total comes from the derived-metrics endpoint, not from summing channels
 * in this tree). Vacant channels are always excluded from `countedActive`.
 */
export function summarizeChildren(node: Pick<ChannelTreeNode, 'children'>): ChildCountSummary {
  let countedActive = 0;
  let vacant = 0;
  let other = 0;
  for (const child of node.children) {
    if (isVacant(child.channel)) {
      vacant++;
    } else if (child.channel.countsTowardTotal && child.channel.state === 'active') {
      countedActive++;
    } else {
      other++;
    }
  }
  return { countedActive, vacant, other, total: node.children.length };
}
